import { useState } from 'react';
import { HardDrive } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toaster';
import type { WorkGroupAdminQuotaRow } from '@/lib/api';
import {
  useAdminWorkGroupQuotas,
  useUpdateAdminWorkGroupQuota,
  workgroupsMutationErrorMessage,
} from '@/hooks/use-workgroups';

function formatBytesLabel(value: string): string {
  try {
    const n = BigInt(value);
    if (n === 0n) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let v = Number(n);
    let u = 0;
    while (v >= 1024 && u < units.length - 1) {
      v /= 1024;
      u += 1;
    }
    const digits = u === 0 ? 0 : v < 10 ? 2 : v < 100 ? 1 : 0;
    return `${v.toFixed(digits)} ${units[u]}`;
  } catch {
    return value;
  }
}

function mbToBytes(mb: string): bigint | null {
  const n = Number(mb);
  if (!Number.isFinite(n) || n < 0) return null;
  return BigInt(Math.round(n * 1024 * 1024));
}

function QuotaRow({ row }: { row: WorkGroupAdminQuotaRow }) {
  const updateMutation = useUpdateAdminWorkGroupQuota();
  const used = BigInt(row.usedBytes);
  const [mbInput, setMbInput] = useState(() =>
    String(Number(row.quotaBytes) / (1024 * 1024)),
  );

  const handleSave = () => {
    const bytes = mbToBytes(mbInput);
    if (bytes === null) {
      toast({ title: 'Enter a valid quota in MB', variant: 'destructive' });
      return;
    }
    if (bytes < used) {
      toast({
        title: 'Quota cannot be below current usage',
        description: `Used: ${formatBytesLabel(row.usedBytes)}`,
        variant: 'destructive',
      });
      return;
    }
    updateMutation.mutate(
      { userId: row.userId, quotaBytes: bytes.toString() },
      {
        onSuccess: () => toast({ title: 'Quota updated' }),
        onError: (err) => {
          toast({ title: workgroupsMutationErrorMessage(err), variant: 'destructive' });
        },
      },
    );
  };

  return (
    <tr className="border-b border-border last:border-0">
      <td className="px-3 py-2 text-sm text-foreground">
        <div className="font-medium">{row.displayName?.trim() || row.email}</div>
        <div className="text-xs text-muted-foreground">{row.email}</div>
      </td>
      <td className="px-3 py-2 text-sm text-muted-foreground">{formatBytesLabel(row.usedBytes)}</td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="number"
            min={0}
            step={1}
            className="h-8 w-28"
            value={mbInput}
            onChange={(e) => setMbInput(e.target.value)}
            aria-label={`Quota MB for ${row.email}`}
          />
          <span className="text-xs text-muted-foreground">MB</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={updateMutation.isPending}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </td>
    </tr>
  );
}

export function WorkGroupAdminQuotasPanel() {
  const { data: quotas, isLoading, error } = useAdminWorkGroupQuotas();

  return (
    <Card className="border-border">
      <CardHeader className="space-y-1">
        <div className="flex items-center gap-2 text-foreground">
          <HardDrive className="h-4 w-4" aria-hidden />
          <CardTitle className="text-base">Private storage quotas</CardTitle>
        </div>
        <CardDescription>
          Manage per-user private WorkGroup storage limits. Project work groups are not quota-bound.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? <p className="text-sm text-muted-foreground">Loading quotas…</p> : null}
        {error ? <p className="text-sm text-destructive">Could not load admin quotas.</p> : null}
        {quotas && quotas.length === 0 && !isLoading ? (
          <p className="text-sm text-muted-foreground">No quota rows yet.</p>
        ) : null}
        {quotas && quotas.length > 0 ? (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full min-w-[32rem] text-left">
              <thead className="bg-muted/50 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">User</th>
                  <th className="px-3 py-2 font-medium">Used</th>
                  <th className="px-3 py-2 font-medium">Quota</th>
                </tr>
              </thead>
              <tbody>
                {quotas.map((row) => (
                  <QuotaRow key={row.userId} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
